/* CUSTOM JS */

class BBNotificationManager {
  constructor() {
    this.container = null;
  }

  getContainer() {
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.className = 'bb-notification-container';
      this.container.setAttribute('role', 'region');
      this.container.setAttribute('aria-label', 'Notifications');
      document.body.insertBefore(this.container, document.body.firstChild);
    }
    return this.container;
  }

  show(options = {}) {
    const { text = '', linkText = '', link = '', duration = 5000, showCloseButton = true } = options;

    const notification = document.createElement('div');
    notification.className = 'bb-notification';
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');

    let html = '';
    if (text) html += `<p class="bb-notification__text">${text}</p>`;

    const hasActions = (linkText && link) || showCloseButton;
    if (hasActions) {
      html += '<div class="bb-notification__actions">';
      if (linkText && link) html += `<a href="${link}" class="bb-notification__link">${linkText}</a>`;
      if (showCloseButton)
        html += `<button type="button" class="bb-notification__close" aria-label="Close notification"><svg width="24" height="24" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.319 3.94506C22.095 3.95035 21.8823 4.04402 21.7272 4.20561L13.5651 12.3677L5.40302 4.20561C5.32398 4.12422 5.22942 4.05952 5.12493 4.01533C5.02044 3.97115 4.90815 3.94838 4.7947 3.94837C4.62595 3.94841 4.46106 3.9988 4.32111 4.09308C4.18117 4.18736 4.07253 4.32125 4.00911 4.47762C3.94568 4.63399 3.93035 4.80573 3.96508 4.97086C3.9998 5.13599 4.08301 5.28701 4.20404 5.40459L12.3661 13.5667L4.20404 21.7288C4.12267 21.8069 4.0577 21.9005 4.01295 22.004C3.96819 22.1076 3.94455 22.219 3.9434 22.3318C3.94226 22.4446 3.96363 22.5565 4.00627 22.661C4.04891 22.7654 4.11196 22.8603 4.19173 22.9401C4.27149 23.0198 4.36638 23.0829 4.47082 23.1255C4.57526 23.1682 4.68716 23.1895 4.79996 23.1884C4.91276 23.1872 5.0242 23.1636 5.12776 23.1188C5.23131 23.0741 5.32489 23.0091 5.40302 22.9277L13.5651 14.7657L21.7272 22.9277C21.8053 23.0091 21.8989 23.0741 22.0025 23.1188C22.106 23.1636 22.2175 23.1872 22.3303 23.1884C22.4431 23.1895 22.555 23.1682 22.6594 23.1255C22.7638 23.0829 22.8587 23.0198 22.9385 22.9401C23.0183 22.8603 23.0813 22.7654 23.1239 22.661C23.1666 22.5565 23.188 22.4446 23.1868 22.3318C23.1857 22.219 23.162 22.1076 23.1173 22.004C23.0725 21.9005 23.0075 21.8069 22.9262 21.7288L14.7641 13.5667L22.9262 5.40459C23.0495 5.28624 23.1341 5.13341 23.1691 4.96611C23.204 4.79882 23.1876 4.62487 23.1219 4.46708C23.0563 4.30929 22.9445 4.17501 22.8012 4.08186C22.6579 3.98871 22.4898 3.94102 22.319 3.94506Z" fill="white"/></svg></button>`;
      html += '</div>';
    }

    notification.innerHTML = html;

    let progressBar = null;
    if (duration > 0) {
      progressBar = document.createElement('div');
      progressBar.className = 'bb-notification__progress';
      notification.appendChild(progressBar);
    }

    this.getContainer().appendChild(notification);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        notification.classList.add('bb-notification--visible');
        if (progressBar && duration > 0) {
          progressBar.style.transitionDuration = `${duration}ms`;
          progressBar.style.transform = 'scaleX(0)';
        }
      });
    });

    let timeout = null;

    function close() {
      if (timeout) clearTimeout(timeout);
      notification.classList.remove('bb-notification--visible');
      setTimeout(() => notification.remove(), 200);
    }

    if (showCloseButton) {
      notification.querySelector('.bb-notification__close')?.addEventListener('click', close);
    }

    if (duration > 0) {
      timeout = setTimeout(close, duration);
    }

    return { close };
  }
}

class CdpSessionManager {
  static SESSION_KEY = 'cdp::session_id';
  static POLL_INTERVAL = 200;
  static POLL_TIMEOUT = 5000;

  constructor() {
    this.cachedPromise = null;
  }

  getFromCookie() {
    const match = document.cookie.match(/(^|; )_shopify_y=([^;]*)/);
    return match ? decodeURIComponent(match[2]) : null;
  }

  get() {
    if (this.cachedPromise) return this.cachedPromise;
    this.cachedPromise = new Promise((resolve) => {
      const immediate = localStorage.getItem(CdpSessionManager.SESSION_KEY);
      if (immediate) {
        resolve(immediate);
        return;
      }
      let elapsed = 0;
      const poll = setInterval(() => {
        const value = localStorage.getItem(CdpSessionManager.SESSION_KEY);
        if (value) {
          clearInterval(poll);
          resolve(value);
          return;
        }
        elapsed += CdpSessionManager.POLL_INTERVAL;
        if (elapsed >= CdpSessionManager.POLL_TIMEOUT) {
          clearInterval(poll);
          const cookieId = this.getFromCookie();
          if (!cookieId) console.error('CdpSession: sessionId not resolved');
          resolve(cookieId);
        }
      }, CdpSessionManager.POLL_INTERVAL);
    });
    return this.cachedPromise;
  }

  reset() {
    this.cachedPromise = null;
  }
}

const CdpSession = new CdpSessionManager();

class BBWishlistManager {
  static ATTRIBUTE_KEY = '_bb_wishlist';
  static DEBOUNCE_MS = 300;
  // Same-origin app-proxy endpoint (KOWS-114). The theme no longer talks to the
  // CDP directly: the proxy verifies the Shopify app-proxy signature, resolves
  // the logged-in customer server-side, and holds the CDP credentials. Guests
  // pass their session id as `sid` so their wishlist still persists in the CDP.
  static WISHLIST_ENDPOINT = '/a/kosmos/wishlist';
  static CDP_METAFIELD_KEY = 'cdp--wishlist';

  constructor() {
    this.entries = [];
    this.initialized = false;
    this.initPromise = null;
    this.readyPromise = null;
    this.cdpLoaded = false;
    this.sessionMissing = false;
    this.debounceTimer = null;
  }

  static parseRaw(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .filter(Boolean)
      .map((entry) => {
        const sep = entry.indexOf(':');
        if (sep === -1) return { handle: entry, productId: null };
        return { handle: entry.slice(0, sep), productId: entry.slice(sep + 1) || null };
      });
  }

  static serialize(entries) {
    return entries.map((e) => (e.productId ? `${e.handle}:${e.productId}` : e.handle)).join(',');
  }

  static VALID_FORMAT = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?:\d+)(,[a-z0-9]([a-z0-9-]*[a-z0-9])?:\d+)*$/;

  static isValidFormat(raw) {
    return !raw || BBWishlistManager.VALID_FORMAT.test(raw);
  }

  handles() {
    return this.entries.map((e) => e.handle);
  }

  fetchFromCdp() {
    return CdpSession.get()
      .then((sessionId) => {
        this.sessionMissing = !sessionId;
        if (!sessionId) return null;
        const params = new URLSearchParams({ product_fragment: 'handle,id', sid: sessionId });
        return fetch(`${BBWishlistManager.WISHLIST_ENDPOINT}?${params}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (!data || !Array.isArray(data.value)) return null;
            return data.value
              .map((item) => {
                const handle = item.shopifyProduct?.handle || null;
                const rawId = item.shopifyProduct?.id ?? null;
                const productId = rawId != null ? String(rawId).replace(/\D/g, '') : null;
                return { handle, productId };
              })
              .filter((e) => e.handle && e.productId);
          });
      })
      .catch(() => null);
  }

  init() {
    if (this.initPromise) return this.initPromise;
    this.initPromise = Promise.all([fetch(`${Shopify.routes.root}cart.js`).then((r) => r.json()), this.fetchFromCdp()])
      .then(([cart, cdpEntries]) => {
        const raw = cart.attributes?.[BBWishlistManager.ATTRIBUTE_KEY] || '';
        const formatValid = BBWishlistManager.isValidFormat(raw);
        const cartEntries = formatValid ? BBWishlistManager.parseRaw(raw).filter((e) => e.productId) : [];
        this.initialized = true;
        this.cdpLoaded = cdpEntries !== null;

        if (cdpEntries !== null) {
          this.entries = cdpEntries;
          if (BBWishlistManager.serialize(cartEntries) !== BBWishlistManager.serialize(cdpEntries)) {
            this.writeCart(cdpEntries);
          }
        } else if (formatValid) {
          this.entries = cartEntries;
        } else {
          this.entries = [];
          this.writeCart([]);
        }
        this.dispatch();
        return this.handles();
      })
      .catch(() => {
        this.entries = [];
        this.initialized = true;
        this.cdpLoaded = false;
        return this.handles();
      });
    return this.initPromise;
  }

  // Mutations wait for this before touching entries: save() PUTs the full list,
  // so mutating before the CDP read would wipe the stored wishlist. If the
  // initial read found no session id (it ran before consent), read once more
  // with a fresh lookup: mutations only happen after consent, when the session
  // id appears. The promise is shared so concurrent clicks wait for one read.
  ready() {
    if (this.readyPromise) return this.readyPromise;
    this.readyPromise = this.init().then(() => {
      if (!this.sessionMissing) return;
      CdpSession.reset();
      this.initPromise = null;
      return this.init();
    });
    return this.readyPromise;
  }

  writeCart(entries) {
    const value = BBWishlistManager.serialize(entries);
    return fetch(`${Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [BBWishlistManager.ATTRIBUTE_KEY]: value } }),
    }).catch(() => {});
  }

  save() {
    this.writeCart(this.entries);
    // Only replace the stored list after reading it, never after a failed read.
    if (this.cdpLoaded) this.syncToCdp();
  }

  syncToCdp() {
    CdpSession.get()
      .then((sessionId) => {
        if (!sessionId) return;
        const value = this.entries.filter((e) => e.productId).map((e) => ({ shopify_product_id: e.productId }));
        const params = new URLSearchParams({ sid: sessionId });
        fetch(`${BBWishlistManager.WISHLIST_ENDPOINT}?${params}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value }),
        }).catch(() => {});
      })
      .catch(() => {});
  }

  dispatch() {
    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        detail: { handles: this.handles() },
      }),
    );
  }

  debouncedSave() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.save(), BBWishlistManager.DEBOUNCE_MS);
  }

  get() {
    return [...this.entries];
  }

  has(handle) {
    return this.entries.some((e) => e.handle === handle);
  }

  async add(handle, productId) {
    await this.ready();
    if (!handle || this.has(handle)) return;
    this.entries.push({ handle, productId: productId || null });
    this.dispatch();
    this.debouncedSave();
  }

  async remove(handle) {
    await this.ready();
    const idx = this.entries.findIndex((e) => e.handle === handle);
    if (idx === -1) return;
    this.entries.splice(idx, 1);
    this.dispatch();
    this.debouncedSave();
  }

  async toggle(handle, productId) {
    await this.ready();
    if (this.has(handle)) {
      await this.remove(handle);
      return false;
    } else {
      await this.add(handle, productId);
      return true;
    }
  }

  async set(newEntries) {
    await this.ready();
    this.entries = [...newEntries];
    this.dispatch();
    this.debouncedSave();
  }
}

const BBNotification = new BBNotificationManager();
const BBWishlist = new BBWishlistManager();

class BBWishlistConsentManager {
  static DRAWER_ID = 'wishlist-consent-drawer';
  static UC_SERVICES = ['Shopify Analytics', 'Shopify Marketing'];

  constructor() {
    this.ready = new Promise((resolve) => {
      if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
        resolve();
      } else {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => resolve());
      }
    });
  }

  hasConsented() {
    const cp = window.Shopify?.customerPrivacy;
    if (!cp) return false;
    return cp.analyticsProcessingAllowed() && cp.marketingAllowed();
  }

  grantConsent() {
    const cp = window.Shopify?.customerPrivacy;
    if (cp?.setTrackingConsent) {
      cp.setTrackingConsent({ analytics: true, marketing: true }, () => {});
    }
    this.grantUsercentricsConsent();
  }

  // Usercentrics pushes its own state into Shopify on every page load
  // (layout/theme.liquid), so the consent must be saved there as well, or the
  // next page resets it and the drawer shows up again.
  // https://usercentrics.com/docs/web/features/api/control-functionality/
  async grantUsercentricsConsent() {
    try {
      const uc = await BBWishlistConsentManager.usercentricsReady();
      const { services } = await uc.getConsentDetails();
      const updates = Object.entries(services)
        .filter(([, service]) => BBWishlistConsentManager.UC_SERVICES.includes(service.name))
        .map(([id]) => ({ id, consent: true }));
      if (!updates.length) return;
      await uc.updateServicesConsents(updates);
      await uc.saveConsents();
    } catch (e) {}
  }

  // __ucCmp methods are only safe to call after UC_UI_INITIALIZED:
  // https://usercentrics.com/docs/web/features/events/uc-ui-initialized/
  // isInitialized() returns a Promise, so it has to be awaited. A call made in
  // the same task as the initialization never resolves, so continue in a new one.
  static async usercentricsReady() {
    const uc = window.__ucCmp;
    if (!uc || !(await uc.isInitialized())) {
      await new Promise((resolve) => window.addEventListener('UC_UI_INITIALIZED', resolve, { once: true }));
    }
    await new Promise((resolve) => setTimeout(resolve));
    return window.__ucCmp;
  }

  requestConsent() {
    return new Promise((resolve) => {
      const drawer = document.getElementById(BBWishlistConsentManager.DRAWER_ID);
      if (!drawer) {
        resolve(false);
        return;
      }
      const ac = new AbortController();
      drawer.querySelectorAll('[data-wishlist-consent]').forEach((btn) => {
        btn.addEventListener(
          'click',
          (e) => {
            const accepted = e.currentTarget.dataset.wishlistConsent === 'accept';
            drawer.hide();
            ac.abort();
            if (accepted) this.grantConsent();
            resolve(accepted);
          },
          { signal: ac.signal },
        );
      });
      drawer.show();
    });
  }

  async gate() {
    await this.ready;
    if (this.hasConsented()) return true;
    return this.requestConsent();
  }
}

const BBWishlistConsent = new BBWishlistConsentManager();

class BBWishlistButton extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onWishlistChange = this._onWishlistChange.bind(this);
  }

  connectedCallback() {
    BBWishlist.init();
    this.addEventListener('click', this._onClick);
    document.addEventListener('wishlist:change', this._onWishlistChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('wishlist:change', this._onWishlistChange);
  }

  _onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const handle = this.getAttribute('handle');
    if (!handle) return;
    BBWishlistConsent.gate().then(async (allowed) => {
      if (!allowed) return;
      const productId = this.getAttribute('product-id') || null;
      const added = await BBWishlist.toggle(handle, productId);
      if (added) {
        const text = this.getAttribute('notification-text');
        if (text) document.dispatchEvent(new CustomEvent('wishlist:added', { detail: { text } }));
      }
    });
  }

  _onWishlistChange(e) {
    this.dataset.active = e.detail.handles.includes(this.getAttribute('handle')).toString();
  }
}

if (!customElements.get('bb-wishlist-button')) {
  customElements.define('bb-wishlist-button', BBWishlistButton);
}
class CollectionTabsPanel extends HTMLElement {
  connectedCallback() {
    this._resizeObserver = new ResizeObserver(() => {
      if (!this.hidden) {
        const scrollCarousel = this.querySelector('scroll-carousel');
        if (scrollCarousel) {
          const isScrollable = scrollCarousel.scrollWidth > scrollCarousel.clientWidth;
          scrollCarousel.classList.toggle('is-scrollable', isScrollable);
        }
      }
    });
    this._resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
  }
}

if (!customElements.get('collection-tabs-panel')) {
  customElements.define('collection-tabs-panel', CollectionTabsPanel);
}

class CollectionWorlds extends HTMLElement {
  connectedCallback() {
    this._carousels = this.querySelectorAll('scroll-carousel[color-worlds]');
    this._handleChange = this._handleChange.bind(this);
    this._handleTabClick = this._handleTabClick.bind(this);

    this._carousels.forEach((carousel) => {
      carousel.addEventListener('carousel:change', this._handleChange);
      carousel.addEventListener('carousel:settle', this._handleChange);
    });

    this.addEventListener('click', this._handleTabClick);

    requestAnimationFrame(() => {
      const firstCarousel = this._carousels[0];
      if (firstCarousel) {
        this._applyColors(firstCarousel, 0);
      }
    });
  }

  disconnectedCallback() {
    this._carousels?.forEach((carousel) => {
      carousel.removeEventListener('carousel:change', this._handleChange);
      carousel.removeEventListener('carousel:settle', this._handleChange);
    });
    this.removeEventListener('click', this._handleTabClick);
  }

  _handleTabClick(e) {
    if (!e.target.closest('[role="tab"]')) return;

    const clickedButton = e.target.closest('[role="tab"]');
    const panelId = clickedButton.getAttribute('controlled-panel');
    const panel = document.getElementById(panelId);
    const carousel = panel?.querySelector('scroll-carousel[color-worlds]');

    if (carousel && carousel.offsetParent !== null) {
      this._applyColors(carousel, 0);
    } else if (carousel) {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this._applyColors(carousel, 0);
          observer.disconnect();
        }
      });
      observer.observe(carousel);
    }
  }

  _handleChange(e) {
    this._applyColors(e.currentTarget, e.detail?.index ?? 0);
  }

  _applyColors(carousel, index) {
    const items = carousel.querySelectorAll('.collection-card');
    const item = items[index];
    if (!item) return;

    this.style.setProperty('--world-text-color', item.dataset.worldTextColor || '');
    this.style.setProperty('--world-title-color', item.dataset.worldTitleColor || '');
    this.style.setProperty('--world-section-bg', item.dataset.worldSectionBg || '');
    this.style.setProperty('--world-section-bg-gradient', item.dataset.worldSectionBgGradient || '');
    this.style.setProperty('--world-tab-bg-active', item.dataset.worldTabBgActive || '');
    this.style.setProperty('--world-tab-text-active', item.dataset.worldTabTextActive || '');
    this.style.setProperty('--world-tab-bg-inactive', item.dataset.worldTabBgInactive || '');
    this.style.setProperty('--world-tab-text-inactive', item.dataset.worldTabTextInactive || '');
    this.style.setProperty('--world-tab-border-active', item.dataset.worldTabBorderActive || '');
    this.style.setProperty('--world-tab-border-inactive', item.dataset.worldTabBorderInactive || '');
  }
}

if (!customElements.get('collection-worlds')) {
  customElements.define('collection-worlds', CollectionWorlds);
}

class BBMobileSearchCollapse {
  constructor(header) {
    this.header = header;
    this.mode = header.dataset.mobileSearchCollapse || 'scroll';
    this.toggleButton = header.querySelector('.bb-mobile-search-toggle');
    this.searchInput = header.querySelector('bb-inline-search input[name="q"]');
    this.scrollPaused = false;
    this.resumeTimer = null;

    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    this.toggleButton?.addEventListener('click', () => this.handleToggleClick());
    this.searchInput?.addEventListener('keydown', (event) => this.handleKeydown(event));

    if (this.mode !== 'manual') {
      this.handleScroll();
    }
  }

  handleScroll() {
    if (this.scrollPaused) return;

    if (window.scrollY > 105) {
      this.header.classList.add('header--search-hidden');
    } else if (window.scrollY < 5 && this.mode === 'scroll') {
      this.header.classList.remove('header--search-hidden');
    }
  }

  handleToggleClick() {
    this.header.classList.remove('header--search-hidden');
    this.searchInput?.focus({ preventScroll: true });

    this.scrollPaused = true;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      this.scrollPaused = false;
    }, 400);
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;

    this.searchInput.blur();

    if (window.scrollY > 105 || this.mode === 'manual') {
      this.header.classList.add('header--search-hidden');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('store-header[data-mobile-search-collapse]');
  if (header) new BBMobileSearchCollapse(header);
});

class BBInlineSearch extends HTMLElement {
  constructor() {
    super();
    this.isMenuOpen = false;
    this.searchInput = null;
    this.megaMenu = null;
    this.searchSubmit = null;
    this.form = null;
    this.predictiveSearch = null;
    this.mutationObserver = null;

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleMegaMenuClick = this.handleMegaMenuClick.bind(this);
    this.handleSearchSubmit = this.handleSearchSubmit.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleHeaderTransparency = this.handleHeaderTransparency.bind(this);
  }

  connectedCallback() {
    this.findElements();
    if (!this.searchInput || !this.megaMenu) {
      return;
    }
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.cleanupObserver();
  }

  findElements() {
    this.searchInput = this.querySelector('input[name="q"]');
    this.megaMenu = this.querySelector('#bb-inline-search-mega-menu');
    this.searchSubmit = this.querySelector('[data-search-submit]');
    this.inputCloseButton = this.querySelector('[data-inline-search-input-close]');
    this.form = this.querySelector('form');
    this.predictiveSearch = this.querySelector('predictive-search');
  }

  setupEventListeners() {
    this.searchInput.addEventListener('input', this.handleInputChange);
    this.searchInput.addEventListener('focus', this.handleInputFocus);
    this.searchInput.addEventListener('keydown', this.handleKeydown);
    this.megaMenu.addEventListener('click', this.handleMegaMenuClick);

    if (this.searchSubmit) {
      this.searchSubmit.addEventListener('click', this.handleSearchSubmit);
    }

    if (this.inputCloseButton) {
      this.inputCloseButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.searchInput.value = '';
        this.closeMegaMenu();
      });
    }

    this.setupPredictiveSearchObserver();
  }

  setupPredictiveSearchObserver() {
    if (this.predictiveSearch) {
      this.mutationObserver = new MutationObserver(() => {
        this.handlePredictiveSearchChange();
      });
      this.mutationObserver.observe(this.predictiveSearch, { childList: true, subtree: true });
    }
  }

  cleanupObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  removeEventListeners() {
    if (this.searchInput) {
      this.searchInput.removeEventListener('input', this.handleInputChange);
      this.searchInput.removeEventListener('focus', this.handleInputFocus);
      this.searchInput.removeEventListener('keydown', this.handleKeydown);
    }

    if (this.megaMenu) {
      this.megaMenu.removeEventListener('click', this.handleMegaMenuClick);
    }

    if (this.searchSubmit) {
      this.searchSubmit.removeEventListener('click', this.handleSearchSubmit);
    }

    document.removeEventListener('click', this.handleClickOutside);
  }

  handleInputChange() {
    const inputValue = this.searchInput.value.trim();
    const enableIdleState = this.hasAttribute('data-enable-idle-state');

    if (enableIdleState) {
      if (inputValue.length > 0 || this.isMenuOpen) {
        this.openMegaMenu();
      }
    } else {
      this.toggleMegaMenu(inputValue.length > 0);
    }
  }

  handleInputFocus() {
    const inputValue = this.searchInput.value.trim();
    const enableIdleState = this.hasAttribute('data-enable-idle-state');

    if (enableIdleState || inputValue.length > 0) {
      this.openMegaMenu();
    }
  }

  handleMegaMenuClick(event) {
    event.stopPropagation();
  }

  handleSearchSubmit(event) {
    event.preventDefault();
    const searchValue = this.searchInput.value.trim();
    if (this.form && searchValue !== '') {
      this.form.submit();
    }
  }

  handlePredictiveSearchChange() {
    const inputValue = this.searchInput.value.trim();
    if (inputValue.length > 0 && this.predictiveSearch) {
      const hasResults = this.predictiveSearch.querySelector('[slot="results"]');
      if (hasResults) {
        this.openMegaMenu();
      }
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      this.closeMegaMenu();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isMenuOpen) {
      this.closeMegaMenu();
      this.searchInput.blur();
    }
  }

  toggleMegaMenu(shouldOpen) {
    if (shouldOpen) {
      this.openMegaMenu();
    } else {
      this.closeMegaMenu();
    }
  }

  openMegaMenu() {
    if (this.isMenuOpen) return;
    this.isMenuOpen = true;
    this.megaMenu.classList.add('is-open');
    this.megaMenu.setAttribute('open', '');
    if (this.inputCloseButton) this.inputCloseButton.hidden = false;
    document.addEventListener('click', this.handleClickOutside);
    this.triggerPredictiveSearch();
    this.handleHeaderTransparency();
  }

  closeMegaMenu() {
    if (!this.isMenuOpen) return;
    this.isMenuOpen = false;
    this.megaMenu.classList.remove('is-open');
    this.megaMenu.removeAttribute('open');
    if (this.inputCloseButton) this.inputCloseButton.hidden = true;
    document.removeEventListener('click', this.handleClickOutside);
    this.handleHeaderTransparency();
  }

  triggerPredictiveSearch() {
    if (this.predictiveSearch && this.searchInput) {
      const event = new Event('input', { bubbles: true });
      this.searchInput.dispatchEvent(event);
    }
  }

  handleHeaderTransparency() {
    const header = document.querySelector('store-header');
    if (header && typeof header._checkTransparency === 'function') {
      header._checkTransparency();
    }
  }
}

customElements.define('bb-inline-search', BBInlineSearch);

class BBWishlistLink extends HTMLElement {
  constructor() {
    super();
    this._onWishlistAdded = this._onWishlistAdded.bind(this);
    this._onWishlistChange = this._onWishlistChange.bind(this);
    this._onLinkClick = this._onLinkClick.bind(this);
    this._animating = false;
  }

  connectedCallback() {
    this._overlay = this.querySelector('.bb-wishlist-link-overlay');
    this._overlayText = this.querySelector('.bb-wishlist-link-overlay__text-inner');
    this._countBubble = this.querySelector('[data-wishlist-count]');
    this._countText = this._countBubble?.querySelector('[aria-hidden="true"]');
    document.addEventListener('wishlist:added', this._onWishlistAdded);
    document.addEventListener('wishlist:change', this._onWishlistChange);
    this.addEventListener('click', this._onLinkClick);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:added', this._onWishlistAdded);
    document.removeEventListener('wishlist:change', this._onWishlistChange);
    this.removeEventListener('click', this._onLinkClick);
  }

  _onLinkClick(e) {
    if (!BBWishlistConsent.isEnabled() || BBWishlistConsent.hasConsented()) return;
    e.preventDefault();
    e.stopPropagation();
    BBWishlistConsent.gate().then((allowed) => {
      if (!allowed) return;
      const link = this.querySelector('a[href]');
      if (link) window.location.href = link.href;
    });
  }

  _onWishlistChange(e) {
    const count = e.detail?.handles?.length || 0;
    if (this._countText) this._countText.textContent = count;
    if (!this._animating && this._countBubble) this._countBubble.classList.toggle('opacity-0', count === 0);
  }

  _onWishlistAdded(e) {
    if (this._animating || !this._overlay) return;
    this._animating = true;

    const text = e.detail?.text || '';
    if (this._overlayText) this._overlayText.textContent = text;

    if (this._countBubble) this._countBubble.classList.add('opacity-0');

    const overlay = this._overlay;
    overlay.classList.remove('is-animating');
    void overlay.offsetWidth;
    overlay.classList.add('is-animating');

    overlay.addEventListener(
      'animationend',
      () => {
        overlay.classList.remove('is-animating');
        this._animating = false;
        if (this._countBubble && +(this._countText?.textContent || 0) > 0) this._countBubble.classList.remove('opacity-0');
      },
      { once: true },
    );
  }
}

if (!customElements.get('bb-wishlist-link')) {
  customElements.define('bb-wishlist-link', BBWishlistLink);
}

class BBExpandable extends HTMLElement {
  connectedCallback() {
    this._toggleBtn = this.querySelector('[data-expandable-toggle]');
    this._expandBtn = this.querySelector('[data-expandable-button]');
    this._collapseBtn = this.querySelector('[data-collapsable-button]');
    this._items = this.querySelectorAll('[data-expandable-item]');
    if ((!this._toggleBtn && !this._expandBtn && !this._collapseBtn) || !this._items.length) return;
    this._toggle = this._toggle.bind(this);
    this._syncAria = this._syncAria.bind(this);
    this._syncAria();
    this._toggleBtn?.addEventListener('click', this._toggle);
    this._expandBtn?.addEventListener('click', this._toggle);
    this._collapseBtn?.addEventListener('click', this._toggle);
  }

  disconnectedCallback() {
    this._toggleBtn?.removeEventListener('click', this._toggle);
    this._expandBtn?.removeEventListener('click', this._toggle);
    this._collapseBtn?.removeEventListener('click', this._toggle);
  }

  _toggle() {
    this.dataset.expanded = this.dataset.expanded === 'true' ? 'false' : 'true';
    this._syncAria();
  }

  _syncAria() {
    if (!this._toggleBtn) return;
    this._toggleBtn.setAttribute('aria-expanded', this.dataset.expanded === 'true' ? 'true' : 'false');
  }
}

if (!customElements.get('bb-expandable')) {
  customElements.define('bb-expandable', BBExpandable);
}

const updateMobileNextButton = (gallery) => {
  const carousel = gallery.querySelector('media-carousel');
  const nextBtn = gallery.querySelector('.product-gallery__next');
  if (!carousel || !nextBtn) return;
  const isAtEnd = carousel.selectedIndexAmongVisible >= carousel.visibleItems.length - 1;
  nextBtn.toggleAttribute('hidden', isAtEnd);
};

document.addEventListener('carousel:change', (e) => {
  const gallery = e.target.closest('product-gallery');
  if (gallery) updateMobileNextButton(gallery);
});

document.addEventListener('carousel:settle', (e) => {
  const gallery = e.target.closest('product-gallery');
  if (gallery) updateMobileNextButton(gallery);
});

class BBWishlistShareButton extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick(e) {
    e.preventDefault();
    BBWishlist.init().then((handles) => {
      if (!handles.length) return;
      const entries = BBWishlist.get();
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('wishlist', BBWishlistManager.serialize(entries));
      const shareUrl = url.toString();

      if (navigator.share) {
        navigator.share({ url: shareUrl }).catch(() => {
          this._copyFallback(shareUrl);
        });
      } else {
        this._copyFallback(shareUrl);
      }
    });
  }

  _copyFallback(url) {
    navigator.clipboard.writeText(url).catch(() => {});
  }
}

if (!customElements.get('bb-wishlist-share-button')) {
  customElements.define('bb-wishlist-share-button', BBWishlistShareButton);
}

/* wishlist import ?wishlist= query param */
(function () {
  const params = new URLSearchParams(window.location.search);
  const wl = params.get('wishlist');
  if (!wl) return;

  const entries = BBWishlistManager.parseRaw(wl);
  if (!entries.length) return;

  const value = BBWishlistManager.serialize(entries);
  fetch(`${Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _bb_wishlist: value } }),
  })
    .then(() => {
      BBWishlist.entries = entries;
      BBWishlist.initialized = true;
      BBWishlist.syncToCdp();
      const url = new URL(window.location.href);
      url.searchParams.delete('wishlist');
      window.location.replace(url.toString());
    })
    .catch(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete('wishlist');
      window.location.replace(url.toString());
    });
})();

(function initHaptics() {
  if (typeof WebHaptics === 'undefined') return;

  const haptics = new WebHaptics();

  document.addEventListener(
    'submit',
    (e) => {
      if (e.target.matches('[is="product-form"]') || e.target.action?.includes('/cart/add')) {
        haptics.trigger('success');
      }
    },
    true,
  );

  document.addEventListener('wishlist:added', () => {
    haptics.trigger([{ duration: 40 }]);
  });
})();

/* Slideshow Video End-Image */
class BBVideoEndImage extends HTMLElement {
  connectedCallback() {
    this.slide = this.closest('.slideshow__slide');
    if (!this.slide) return;

    this.handleEnded = this.handleEnded.bind(this);

    this.slide.querySelectorAll('video-media video').forEach((video) => {
      video.addEventListener('ended', this.handleEnded);
    });
  }

  disconnectedCallback() {
    this.slide?.querySelectorAll('video-media video').forEach((video) => {
      video.removeEventListener('ended', this.handleEnded);
    });
  }

  handleEnded() {
    this.slide?.classList.add('slideshow__slide--video-ended');
  }
}

if (!customElements.get('bb-video-end-image')) {
  customElements.define('bb-video-end-image', BBVideoEndImage);
}

class BBErsatzteilSearch extends HTMLElement {
  constructor() {
    super();
    this._onInput = this._onInput.bind(this);
    this._onClear = this._onClear.bind(this);
    this._onScanClick = this._onScanClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onEscapeKey = this._onEscapeKey.bind(this);
    this._scanLoop = this._scanLoop.bind(this);
    this._runSearch = this._runSearch.bind(this);
  }

  connectedCallback() {
    this._input = this.querySelector('input[name="q"]');
    this._clearBtn = this.querySelector('[data-ersatzteil-search-clear]');
    this._scanBtn = this.querySelector('[data-ersatzteil-search-scan]');
    this._resultsRegion = this.querySelector('[data-ersatzteil-search-results]');
    this._loadingRegion = this.querySelector('[data-ersatzteil-search-loading]');
    this._debounceTimer = null;
    this._controller = null;
    if (!this._input) return;
    this._input.addEventListener('input', this._onInput);
    if (this._clearBtn) this._clearBtn.addEventListener('click', this._onClear);

    if (this._scanBtn) {
      const scannerId = this.dataset.scannerId;
      this._modal = scannerId ? document.getElementById(scannerId) : null;
      if (this._modal) {
        if (this._modal.parentElement !== document.body) {
          document.body.appendChild(this._modal);
        }
        this._video = this._modal.querySelector('[data-ersatzteil-scanner-video]');
        this._loader = this._modal.querySelector('[data-ersatzteil-scanner-loader]');
        this._errorEl = this._modal.querySelector('[data-ersatzteil-scanner-error]');
        this._hintEl = this._modal.querySelector('[data-ersatzteil-scanner-hint]');
        this._closeBtn = this._modal.querySelector('[data-ersatzteil-scanner-close]');
        this._scanBtn.addEventListener('click', this._onScanClick);
        if (this._closeBtn) this._closeBtn.addEventListener('click', this._onCloseClick);
      } else {
        this._scanBtn.hidden = true;
      }
    }

    this._syncControlVisibility();
  }

  disconnectedCallback() {
    if (this._input) this._input.removeEventListener('input', this._onInput);
    if (this._clearBtn) this._clearBtn.removeEventListener('click', this._onClear);
    if (this._scanBtn) this._scanBtn.removeEventListener('click', this._onScanClick);
    if (this._closeBtn) this._closeBtn.removeEventListener('click', this._onCloseClick);
    this._hideModal();
    this._stopScan();
    this._abortFetch();
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  _onInput() {
    this._syncControlVisibility();
    this._scheduleSearch();
  }

  _onClear() {
    this._input.value = '';
    this._input.focus();
    this._syncControlVisibility();
    this._scheduleSearch();
  }

  _scheduleSearch() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(this._runSearch, 250);
  }

  _abortFetch() {
    if (this._controller) {
      this._controller.abort();
      this._controller = null;
    }
  }

  async _runSearch() {
    if (!this._resultsRegion) return;
    const term = this._input.value.trim();

    this._abortFetch();

    if (!term) {
      await this._showPanel('idle');
      return;
    }

    await this._showPanel('loading');

    this._controller = new AbortController();
    const url =
      `${Shopify.routes.root}search?view=ersatzteile` +
      `&type=product` +
      `&options[unavailable_products]=last` +
      `&q=${encodeURIComponent(term)}` +
      `&section_id=ersatzteile-search-results`;

    try {
      const res = await fetch(url, { signal: this._controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      await this._showPanel('results', html);
    } catch (err) {
      if (err.name === 'AbortError') return;
      await this._showPanel('results', '');
    } finally {
      this._controller = null;
    }
  }

  /* Mirrors Impact's <predictive-search> slot transition (theme.js): fade out current region, swap content, fade in + slide up. */
  async _showPanel(panel, html) {
    if (this._activePanel === panel && panel !== 'results') return;

    const current = this._visibleRegion();
    if (current) {
      try {
        await current.animate({ opacity: [1, 0] }, { duration: 100, easing: 'ease' }).finished;
      } catch (_) {}
    }

    if (this._loadingRegion) this._loadingRegion.hidden = panel !== 'loading';
    if (this._resultsRegion) {
      if (panel === 'results') {
        this._resultsRegion.innerHTML = html ?? '';
        this._resultsRegion.hidden = false;
      } else if (panel === 'idle') {
        this._resultsRegion.innerHTML = '';
        this._resultsRegion.hidden = true;
      } else {
        this._resultsRegion.hidden = true;
      }
    }
    this._activePanel = panel;

    const next = this._visibleRegion();
    if (next) {
      try {
        await next.animate({ opacity: [0, 1], transform: ['translateY(5px)', 'translateY(0)'] }, { duration: 100, easing: 'ease' }).finished;
      } catch (_) {}
    }
  }

  _visibleRegion() {
    if (this._activePanel === 'loading') return this._loadingRegion;
    if (this._activePanel === 'results') return this._resultsRegion;
    return null;
  }

  _syncControlVisibility() {
    const hasValue = this._input.value.trim().length > 0;
    if (this._clearBtn) this._clearBtn.hidden = !hasValue;
    if (this._scanBtn) this._scanBtn.hidden = hasValue;
  }

  _onScanClick() {
    this._showModal();
  }

  _onCloseClick() {
    this._hideModal();
  }

  _onEscapeKey(e) {
    if (e.key === 'Escape') this._hideModal();
  }

  _showModal() {
    if (!this._modal) return;
    this._modal.setAttribute('data-open', '');
    document.addEventListener('keydown', this._onEscapeKey);
    document.documentElement.classList.add('lock');
    this._startScan();
  }

  _hideModal() {
    if (!this._modal) return;
    this._modal.removeAttribute('data-open');
    document.removeEventListener('keydown', this._onEscapeKey);
    document.documentElement.classList.remove('lock');
    this._stopScan();
  }

  async _startScan() {
    this._setError('');
    this._setLoaderVisible(true);

    try {
      this._DetectorCtor = await this._loadDetector();
    } catch (err) {
      this._setLoaderVisible(false);
      this._setError(this._getText('unsupported'));
      return;
    }

    if (!this._DetectorCtor) {
      this._setLoaderVisible(false);
      this._setError(this._getText('unsupported'));
      return;
    }

    try {
      this._stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
    } catch (err) {
      this._setLoaderVisible(false);
      this._setError(this._getText('camera_error'));
      return;
    }

    if (!this._video) return;
    this._video.srcObject = this._stream;
    try {
      await this._video.play();
    } catch (_) {
      /* autoplay may need user gesture; dialog open is one */
    }

    const formats = (this.dataset.barcodeFormats || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      this._detector = new this._DetectorCtor(formats.length ? { formats } : undefined);
    } catch (_) {
      this._detector = new this._DetectorCtor();
    }

    this._setLoaderVisible(false);
    this._scanning = true;
    this._scanLoop();
  }

  _stopScan() {
    this._scanning = false;
    if (this._scanTimer) {
      clearTimeout(this._scanTimer);
      this._scanTimer = null;
    }
    if (this._stream) {
      this._stream.getTracks().forEach((t) => t.stop());
      this._stream = null;
    }
    if (this._video) {
      this._video.pause();
      this._video.srcObject = null;
    }
    this._detector = null;
  }

  async _scanLoop() {
    if (!this._scanning || !this._detector || !this._video) return;
    if (this._video.readyState < 2) {
      this._scanTimer = setTimeout(this._scanLoop, 120);
      return;
    }

    try {
      const results = await this._detector.detect(this._video);
      if (results && results.length > 0) {
        const value = (results[0].rawValue || '').trim();
        if (value) {
          this._handleDetected(value);
          return;
        }
      }
    } catch (_) {
      /* ignore frame errors and try the next frame */
    }

    if (this._scanning) {
      this._scanTimer = setTimeout(this._scanLoop, 200);
    }
  }

  _handleDetected(value) {
    this._hideModal();
    if (!this._input) return;
    this._input.value = value;
    this._input.dispatchEvent(new Event('input', { bubbles: true }));
    this._input.focus();
    this._syncControlVisibility();
  }

  _setError(message) {
    if (!this._errorEl) return;
    this._errorEl.textContent = message || '';
    this._errorEl.hidden = !message;
  }

  _setLoaderVisible(visible) {
    if (this._loader) this._loader.hidden = !visible;
  }

  _getText(key) {
    const tpl = this._modal?.querySelector(`template[data-ersatzteil-scanner-text="${key}"]`);
    return tpl ? tpl.innerHTML.trim() : '';
  }

  async _loadDetector() {
    if ('BarcodeDetector' in window) {
      try {
        const supported = await window.BarcodeDetector.getSupportedFormats();
        if (supported && supported.length > 0) return window.BarcodeDetector;
      } catch (_) {
        /* fall through to polyfill */
      }
    }

    const ns = window.BarcodeDetectionAPI;
    if (ns?.BarcodeDetector) {
      this._configurePolyfill(ns);
      return ns.BarcodeDetector;
    }

    const src = this.dataset.polyfillSrc;
    if (!src) return null;

    if (!BBErsatzteilSearch._polyfillPromise) {
      BBErsatzteilSearch._polyfillPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load barcode polyfill'));
        document.head.appendChild(script);
      });
    }

    await BBErsatzteilSearch._polyfillPromise;

    const loaded = window.BarcodeDetectionAPI;
    if (!loaded?.BarcodeDetector) return null;
    this._configurePolyfill(loaded);
    return loaded.BarcodeDetector;
  }

  _configurePolyfill(ns) {
    if (BBErsatzteilSearch._polyfillConfigured) return;
    if (typeof ns.setZXingModuleOverrides !== 'function') return;
    const wasmSrc = this.dataset.wasmSrc;
    if (!wasmSrc) return;
    ns.setZXingModuleOverrides({
      locateFile: (path) => (path && path.endsWith('.wasm') ? wasmSrc : path),
    });
    BBErsatzteilSearch._polyfillConfigured = true;
  }
}

if (!customElements.get('bb-ersatzteil-search')) {
  customElements.define('bb-ersatzteil-search', BBErsatzteilSearch);
}

/* Back in stock */
class BBBackInStockManager {
  static DRAWER_ID = 'back-in-stock-drawer';

  constructor() {
    this._bound = false;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._bind(), { once: true });
    } else {
      this._bind();
    }
  }

  drawer() {
    return document.getElementById(BBBackInStockManager.DRAWER_ID);
  }

  _bind() {
    if (this._bound) return;
    const drawer = this.drawer();
    if (!drawer) return;
    this._bound = true;

    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-back-in-stock-close]')) {
        drawer.hide();
      }
    });

    drawer.addEventListener('dialog:after-hide', () => {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('customer_posted')) return;
      url.searchParams.delete('customer_posted');
      window.history.replaceState({}, '', url.toString());
    });
  }

  show() {
    const drawer = this.drawer();
    if (!drawer || typeof drawer.show !== 'function') return;
    this._bind();
    drawer.show();
    requestAnimationFrame(() => {
      drawer.querySelector('input[type="email"]')?.focus();
    });
  }
}

const BBBackInStock = new BBBackInStockManager();

class BBBackInStockButton extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    BBBackInStock.show();
  }
}

if (!customElements.get('bb-back-in-stock-button')) {
  customElements.define('bb-back-in-stock-button', BBBackInStockButton);
}

/* Combined range-pair filter slider */
class BBFilterRange extends HTMLElement {
  connectedCallback() {
    this._ac = new AbortController();
    const { signal } = this._ac;

    this.rangeMin = this.querySelector('[data-range-min]');
    this.rangeMax = this.querySelector('[data-range-max]');
    this.numberMin = this.querySelector('[data-number-min]');
    this.numberMax = this.querySelector('[data-number-max]');
    this.group = this.querySelector('.range-group');
    if (!this.rangeMin || !this.rangeMax || !this.group) return;

    this.axisMin = parseFloat(this.dataset.axisMin);
    this.axisMax = parseFloat(this.dataset.axisMax);
    this.vonInputs = Array.from(this.querySelectorAll('[data-von-input]'));
    this.bisInputs = Array.from(this.querySelectorAll('[data-bis-input]'));

    this._restorePosition();
    this._updateFill();

    this.numberMin.addEventListener('focus', () => this.numberMin.select(), { signal });
    this.numberMax.addEventListener('focus', () => this.numberMax.select(), { signal });

    this.rangeMin.addEventListener('input', () => this._onRangeInput('min'), { signal });
    this.rangeMax.addEventListener('input', () => this._onRangeInput('max'), { signal });
    this.rangeMin.addEventListener(
      'change',
      (event) => {
        event.stopPropagation();
        this._commit();
      },
      { signal },
    );
    this.rangeMax.addEventListener(
      'change',
      (event) => {
        event.stopPropagation();
        this._commit();
      },
      { signal },
    );

    this.numberMin.addEventListener('change', () => this._onNumberChange('min'), { signal });
    this.numberMax.addEventListener('change', () => this._onNumberChange('max'), { signal });
  }

  disconnectedCallback() {
    this._ac?.abort();
  }

  _onRangeInput(which) {
    const lo = parseFloat(this.rangeMin.value);
    const hi = parseFloat(this.rangeMax.value);

    // Keep a 1-unit gap so the two thumbs never overlap (mirrors Impact's price range).
    if (which === 'min') {
      this.rangeMin.value = Math.min(lo, hi - 1);
    }
    if (which === 'max') {
      this.rangeMax.value = Math.max(hi, lo + 1);
    }

    this.numberMin.value = this.rangeMin.value;
    this.numberMax.value = this.rangeMax.value;
    this._updateFill();
  }

  _onNumberChange(which) {
    const input = which === 'min' ? this.numberMin : this.numberMax;
    let value = parseInt(input.value, 10);
    if (isNaN(value)) value = which === 'min' ? this.axisMin : this.axisMax;

    if (which === 'min') {
      this.rangeMin.value = value;
    } else {
      this.rangeMax.value = value;
    }

    this._commit();
  }

  _clamp() {
    let lo = parseFloat(this.rangeMin.value);
    let hi = parseFloat(this.rangeMax.value);
    if (isNaN(lo)) lo = this.axisMin;
    if (isNaN(hi)) hi = this.axisMax;
    lo = Math.max(this.axisMin, Math.min(lo, this.axisMax));
    hi = Math.max(this.axisMin, Math.min(hi, this.axisMax));
    if (lo > hi) {
      const swap = lo;
      lo = hi;
      hi = swap;
    }

    this.rangeMin.value = lo;
    this.rangeMax.value = hi;
    this.numberMin.value = lo;
    this.numberMax.value = hi;
    return { lo, hi };
  }

  _updateFill() {
    const span = this.axisMax - this.axisMin || 1;
    const lo = parseFloat(this.rangeMin.value);
    const hi = parseFloat(this.rangeMax.value);
    this.group.style.setProperty('--range-min', `${((lo - this.axisMin) / span) * 100}%`);
    this.group.style.setProperty('--range-max', `${((hi - this.axisMin) / span) * 100}%`);
  }

  _storageKey() {
    const von = this.vonInputs[0] ? this.vonInputs[0].name : '';
    const bis = this.bisInputs[0] ? this.bisInputs[0].name : '';
    return `bb-range:${location.pathname}:${von}:${bis}`;
  }

  _checkedSignature() {
    const von = this.vonInputs
      .filter((input) => input.checked)
      .map((input) => input.value)
      .sort();
    const bis = this.bisInputs
      .filter((input) => input.checked)
      .map((input) => input.value)
      .sort();
    return `${von.join(',')}|${bis.join(',')}`;
  }

  /* Restore the exact handle positions the shopper chose, so the section re-render does not snap them to the nearest real value. */
  _restorePosition() {
    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(this._storageKey()));
    } catch (error) {
      return;
    }
    if (!stored || typeof stored.lo !== 'number' || typeof stored.hi !== 'number') return;

    // Only restore when the saved selection still matches the filters the server rendered as active, otherwise it is stale.
    if (stored.sig !== this._checkedSignature()) {
      try {
        sessionStorage.removeItem(this._storageKey());
      } catch (error) {}
      return;
    }

    const lo = Math.max(this.axisMin, Math.min(stored.lo, this.axisMax));
    const hi = Math.max(this.axisMin, Math.min(stored.hi, this.axisMax));
    this.rangeMin.value = lo;
    this.rangeMax.value = hi;
    this.numberMin.value = lo;
    this.numberMax.value = hi;
  }

  _commit() {
    const { lo, hi } = this._clamp();
    this._updateFill();

    // A handle parked at its default extreme means "no limit on that side", so that group is left untouched.
    const upperUnbounded = hi >= this.axisMax;
    const lowerUnbounded = lo <= this.axisMin;
    const fullRange = upperUnbounded && lowerUnbounded;

    // Upper handle (hi) constrains the "from" group: tick every value <= hi.
    this.vonInputs.forEach((input) => {
      const value = parseFloat(input.value);
      input.checked = !upperUnbounded && !isNaN(value) && value <= hi;
    });

    // Lower handle (lo) constrains the "to" group: tick every value >= lo.
    this.bisInputs.forEach((input) => {
      const value = parseFloat(input.value);
      input.checked = !lowerUnbounded && !isNaN(value) && value >= lo;
    });

    // Persist the raw handle positions so they survive the section re-render instead of snapping to real values.
    try {
      if (fullRange) {
        sessionStorage.removeItem(this._storageKey());
      } else {
        sessionStorage.setItem(this._storageKey(), JSON.stringify({ lo, hi, sig: this._checkedSignature() }));
      }
    } catch (error) {}

    this.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('bb-filter-range')) {
  customElements.define('bb-filter-range', BBFilterRange);
}

/* Combined range-pair active-facet removal */
class BBRangeClear extends HTMLButtonElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick(event) {
    event.preventDefault();
    const section = this.closest('.shopify-section');
    if (!section) return;

    const sectionId = section.id.replace('shopify-section-', '');
    const url = new URL(window.location.href);

    (this.dataset.params || '').split(',').forEach((param) => {
      const key = param.trim();
      if (key) url.searchParams.delete(key);
    });
    url.searchParams.delete('page');
    url.searchParams.set('section_id', sectionId);

    this.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
  }
}

if (!customElements.get('bb-range-clear')) {
  customElements.define('bb-range-clear', BBRangeClear, { extends: 'button' });
}

/* Centered discount popup (extends the theme dialog-element) */
class BBDiscountPopup extends customElements.get('dialog-element') {
  connectedCallback() {
    super.connectedCallback();

    this._overlay = this.querySelector('[data-overlay]');
    this._overlay?.addEventListener('click', this._onOverlayClick, { signal: this._abortController.signal });

    this._codeInput = this.querySelector('[data-code-input]');
    this._copyButton = this.querySelector('[data-copy-button]');
    if (this._codeInput && this._copyButton) {
      this._codeInput.addEventListener('click', this._onCodeClick, { signal: this._abortController.signal });
    }

    if (this.debugMode || this.shouldAppearAutomatically) {
      this._timer = setTimeout(() => this.show(), this.apparitionDelay);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._timer);
  }

  _onOverlayClick = () => this.hide();

  _onCodeClick = () => {
    this._codeInput.select?.();
    this._copyButton.click();
  };

  get initialFocus() {
    return false;
  }

  get shouldLock() {
    return true;
  }

  get apparitionDelay() {
    return parseInt(this.getAttribute('apparition-delay') || 0, 10) * 1000;
  }

  get debugMode() {
    return this.hasAttribute('debug-mode');
  }

  get storageKey() {
    return 'theme:discount-popup-appeared';
  }

  get shouldAppearAutomatically() {
    return !(this.hasAttribute('only-once') && localStorage.getItem(this.storageKey) === 'true');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'open' && this.open && !this.debugMode) {
      localStorage.setItem(this.storageKey, 'true');
    }
  }
}

if (!customElements.get('bb-discount-popup')) {
  customElements.define('bb-discount-popup', BBDiscountPopup);
}

/* Rating histogram tooltip (Esc dismiss per WCAG 1.4.13; hover/focus visibility is pure CSS) */
class BBRatingTooltip extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.matches(':hover, :focus-within')) {
          this.setAttribute('data-dismissed', '');
        }
      },
      { signal }
    );

    this.addEventListener('pointerenter', () => this._updatePosition(), { signal });
    this.addEventListener('focusin', () => this._updatePosition(), { signal });
    this.addEventListener('pointerleave', () => this.removeAttribute('data-dismissed'), { signal });
    this.addEventListener('focusout', () => this.removeAttribute('data-dismissed'), { signal });
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  // Flip the panel above the stars when there is not enough room below (sticky header counts as occupied space above).
  _updatePosition() {
    const panel = this.querySelector('.rating-tooltip__panel');
    if (!panel) return;

    const rect = this.getBoundingClientRect();

    // --sticky-area-height is a calc() expression, so it is resolved by measuring a probe element
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;height:var(--sticky-area-height, 0px);';
    document.body.appendChild(probe);
    const stickyAreaHeight = probe.offsetHeight;
    probe.remove();

    const neededSpace = panel.offsetHeight + 16;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top - stickyAreaHeight;

    this.removeAttribute('data-position');
    if (spaceBelow < neededSpace && spaceAbove > neededSpace) {
      this.setAttribute('data-position', 'above');
    }
  }
}

if (!customElements.get('bb-rating-tooltip')) {
  customElements.define('bb-rating-tooltip', BBRatingTooltip);
}

/* BB-Metaobject-Search
 * The "Autoren" and "Markenwelten" tabs of the predictive search come from the KOSMOS app
 * (/a/kosmos/search/metaobjects in shopify-app-kosmos), because Liquid only sees the first
 * 250 entries of a metaobject type. snippets/predictive-search-metaobjects.liquid renders
 * those tabs with an item <template>; the patched _doPredictiveSearch fetches the app
 * results next to /search/suggest and fills the tabs before the results are swapped in,
 * so x-tabs only ever sees finished tabs. A tab left without results (no match, error,
 * timeout) is dropped. The prototype is patched instead of subclassed because
 * bb-inline-search looks up the <predictive-search> tag by name. */
class BBMetaobjectSearch {
  static ENDPOINT = '/a/kosmos/search/metaobjects';
  static LIMIT = 6;
  static TIMEOUT_MS = 2000;
  static CACHE_SIZE = 50;
  static suggestCache = new Map();
  static resultsCache = new Map();

  static remember(cache, key, value) {
    cache.set(key, value);
    if (cache.size > BBMetaobjectSearch.CACHE_SIZE) cache.delete(cache.keys().next().value);
  }

  // Mirrors Impact's cachedFetch (theme.js): successful responses are reused for the same URL.
  static async fetchSuggest(url, signal) {
    if (BBMetaobjectSearch.suggestCache.has(url)) return BBMetaobjectSearch.suggestCache.get(url);

    const response = await fetch(url, { signal });
    const html = await response.text();
    if (response.ok) BBMetaobjectSearch.remember(BBMetaobjectSearch.suggestCache, url, html);
    return html;
  }

  // Resolves to {} on any failure, so the rest of the search still renders.
  static async fetchResults(types, query, signal, { limit = BBMetaobjectSearch.LIMIT, timeoutMs = BBMetaobjectSearch.TIMEOUT_MS } = {}) {
    const params = new URLSearchParams({ types: types.join(','), q: query, limit: String(limit) });
    if (document.documentElement.lang) params.set('language', document.documentElement.lang);
    const url = `${BBMetaobjectSearch.ENDPOINT}?${params}`;
    if (BBMetaobjectSearch.resultsCache.has(url)) return BBMetaobjectSearch.resultsCache.get(url);

    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, timeoutMs);

    try {
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) return {};
      const { results } = await response.json();
      if (!results || typeof results !== 'object') return {};
      BBMetaobjectSearch.remember(BBMetaobjectSearch.resultsCache, url, results);
      return results;
    } catch (error) {
      return {};
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
    }
  }

  static isValidResult(item) {
    // Same-origin paths only (the app returns `/pages/...`), never `//host` or other schemes.
    return typeof item?.title === 'string' && typeof item.url === 'string' && /^\/(?!\/)/.test(item.url);
  }

  static renderItem(template, { title, url, image }) {
    const link = template.content.firstElementChild.cloneNode(true);
    link.href = `${window.Shopify?.routes?.root || '/'}${url.slice(1)}`;
    link.querySelector('[data-bb-metaobject-search-title]').textContent = title;

    if (typeof image === 'string') {
      try {
        const sized = (width) => {
          const src = new URL(image);
          src.searchParams.set('width', width);
          return src.toString();
        };
        const img = document.createElement('img');
        img.alt = title;
        img.sizes = '96px';
        img.srcset = `${sized(96)} 96w, ${sized(192)} 192w`;
        img.src = sized(192);
        link.prepend(img);
      } catch (error) {
        // Invalid image URL: show the result without a thumbnail.
      }
    }

    return link;
  }

  // Fills the app tabs of a parsed [slot="results"], drops tabs that stay empty, and shows the empty state when no tab is left.
  static fill(resultsSlot, resultsByType) {
    const panels = resultsSlot?.querySelectorAll('[slot="content"][data-bb-metaobject-search-types]') ?? [];
    if (panels.length === 0) return;

    let emptyText = '';

    panels.forEach((panel) => {
      const template = panel.querySelector('template[data-bb-metaobject-search-item]');
      const limit = Number(panel.dataset.bbMetaobjectSearchLimit) || 0;
      const items = panel.dataset.bbMetaobjectSearchTypes
        .split(' ')
        .flatMap((type) => (Array.isArray(resultsByType[type]) ? resultsByType[type] : []))
        .filter(BBMetaobjectSearch.isValidResult)
        .slice(0, limit);

      if (template) {
        items.forEach((item) => panel.insertBefore(BBMetaobjectSearch.renderItem(template, item), template));
        template.remove();
      }

      if (!panel.querySelector('.predictive-search-result')) {
        emptyText = panel.dataset.bbEmptyText || emptyText;
        resultsSlot.querySelector(`[slot="title"][data-bb-metaobject-search-tab="${panel.dataset.bbMetaobjectSearchTab}"]`)?.remove();
        panel.remove();
      }
    });

    const tabs = resultsSlot.querySelector('x-tabs');
    if (tabs && !tabs.querySelector(':scope > [slot="title"]')) {
      const emptyState = document.createElement('div');
      emptyState.className = 'empty-state';
      const message = document.createElement('p');
      message.className = 'h6';
      message.textContent = emptyText;
      emptyState.append(message);
      tabs.replaceWith(emptyState);
    }
  }
}

const ImpactPredictiveSearch = customElements.get('predictive-search');

if (ImpactPredictiveSearch && !ImpactPredictiveSearch.prototype._bbMetaobjectSearch) {
  ImpactPredictiveSearch.prototype._bbMetaobjectSearch = true;

  // Same steps as Impact's _doPredictiveSearch (theme.js), plus the app results fetched in parallel.
  ImpactPredictiveSearch.prototype._doPredictiveSearch = async function () {
    await this._transitionToSlot('loading');

    const query = this._queryInput.value;
    const { signal } = this._abortController;
    let queryParams = `q=${encodeURIComponent(query)}&section_id=${this.getAttribute('section-id')}&resources[limit]=10&resources[limit_scope]=each`;
    if (this.hasAttribute('search-fields')) {
      queryParams += `&resources[options][fields]=${encodeURIComponent(this.getAttribute('search-fields'))}`;
    }
    const types = (this.dataset.bbMetaobjectSearchTypes || '').split(/[\s,]+/).filter(Boolean);

    const [html, appResults] = await Promise.all([
      BBMetaobjectSearch.fetchSuggest(`${window.Shopify.routes.root}search/suggest?${queryParams}`, signal),
      types.length > 0 ? BBMetaobjectSearch.fetchResults(types, query, signal) : {},
    ]);

    const results = new DOMParser().parseFromString(html, 'text/html').querySelector('[slot="results"]');
    BBMetaobjectSearch.fill(results, appResults);
    this.querySelector('[slot="results"]').replaceWith(document.importNode(results, true));
    return this._transitionToSlot('results');
  };
}

/* BB-Search-Author-Links
 * Links to the authors matching the terms on the search results page (snippets/bb-search-authors.liquid).
 * Liquid only finds an author by the exact full name, so partial names like "Teuber" are matched by the
 * KOSMOS app, through the same endpoint as the "Autoren" tab of the predictive search. The author whose
 * products Liquid already shows is excluded. The element stays hidden when nothing matches or the app fails. */
class BBSearchAuthorLinks extends HTMLElement {
  static TYPE = 'contributor';
  static TIMEOUT_MS = 5000;

  connectedCallback() {
    this._abortController = new AbortController();
    this._render(this._abortController.signal);
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  async _render(signal) {
    const terms = (this.getAttribute('terms') || '').trim();
    const list = this.querySelector('[data-bb-search-author-links-list]');
    const template = this.querySelector('template');
    if (!terms || !list || !template) return;

    const limit = Number(this.getAttribute('limit')) || BBMetaobjectSearch.LIMIT;
    const exclude = new Set((this.getAttribute('exclude') || '').split(/\s+/).filter(Boolean));
    const results = await BBMetaobjectSearch.fetchResults([BBSearchAuthorLinks.TYPE], terms, signal, {
      limit: Math.min(limit + exclude.size, 20),
      timeoutMs: BBSearchAuthorLinks.TIMEOUT_MS,
    });
    if (signal.aborted) return;

    const authors = (Array.isArray(results[BBSearchAuthorLinks.TYPE]) ? results[BBSearchAuthorLinks.TYPE] : [])
      .filter((author) => BBMetaobjectSearch.isValidResult(author) && !exclude.has(author.handle))
      .slice(0, limit);
    if (authors.length === 0) return;

    list.replaceChildren(
      ...authors.map((author) => {
        const link = BBMetaobjectSearch.renderItem(template, author);
        const image = link.querySelector('img');
        if (image) {
          image.alt = ''; // The name next to it is the link text.
        } else {
          link.prepend(BBSearchAuthorLinks.renderInitials(author.title));
        }

        const item = document.createElement('li');
        item.append(link);
        return item;
      }),
    );
    this.hidden = false;
  }

  // Same fallback as the author pages: first letters of the first and the last name.
  static renderInitials(name) {
    const words = name.trim().split(/\s+/);
    const initials = document.createElement('span');
    initials.className = 'bb-search-author-link__initials';
    initials.setAttribute('aria-hidden', 'true');
    initials.textContent = `${words[0]?.charAt(0) ?? ''}${words.length > 1 ? words[words.length - 1].charAt(0) : ''}`.toUpperCase();
    return initials;
  }
}

if (!customElements.get('bb-search-author-links')) {
  customElements.define('bb-search-author-links', BBSearchAuthorLinks);
}
